import { HashStore, LoggerService } from '@pulse/browser-shared';
import { ConsentChange, ConsentItem, ConsentResult, TrackerConfigurations } from '@pulse/common';
import {
    BehaviorSubject,
    Observable,
    ReplaySubject,
    combineLatest,
    distinctUntilChanged,
    filter,
    from,
    isObservable,
    map,
    merge,
} from 'rxjs';
import { OneTrustConsentService } from './one-trust.consent.service';

interface InitializedConsentItem extends ConsentItem {
    result: Observable<ConsentResult>;
}

export class TrackingConsentService {
    private readonly trackerConsentStream: Observable<boolean>;
    private consentChanges: Observable<ConsentChange>;

    constructor(
        private oneTrust: OneTrustConsentService,
        private logger: LoggerService,
        private hashStore: HashStore,
        private config: TrackerConfigurations,
    ) {
        const consentChecks = this.buildConsentChecks(this.config.consentChecks || []);
        const changes = consentChecks.map((consent) =>
            consent.result.pipe(
                map(
                    (result): ConsentChange => ({
                        consent: {
                            consent: consent.consent,
                            mode: consent.mode,
                            report: consent.report,
                            type: consent.type,
                        },
                        result,
                    }),
                ),
            ),
        );
        this.consentChanges = merge(...changes);
        this.trackerConsentStream = this.createTrackerCheckerStream(consentChecks);
    }

    trackingEnabled(): Observable<boolean> {
        return this.trackerConsentStream;
    }

    /**
     * Emits a result object every time it calculates consent result.
     * It will emit values at least once per document load.
     */
    softChanges(): Observable<ConsentChange> {
        return this.consentChanges;
    }

    /**
     * Emits when the consent result is changed compared to previously stored values.
     */
    changes(): Observable<ConsentChange> {
        return this.consentChanges.pipe(
            filter(({ consent, result }) => {
                const provider =
                    typeof consent.consent === 'string' ? consent.consent : consent.consent.name;
                const key = `tracking_consent_${consent.type}${provider}`;
                const changed = this.hashStore.changed(
                    key,
                    result as unknown as Record<string, unknown>,
                );

                return changed;
            }),
        );
    }

    private createTrackerCheckerStream(
        consentChecks: InitializedConsentItem[],
    ): Observable<boolean> {
        const enabledDefault = consentChecks.every((c) => c.mode === 'opt-out');
        const stream = new BehaviorSubject<boolean>(enabledDefault);

        const streams = consentChecks.map((i) =>
            i.result.pipe(
                map((result) => {
                    if (i.type === 'tracking') {
                        if (typeof result.performance_cookies === 'boolean') {
                            return result.performance_cookies;
                        } else if (result.state === 'pending') {
                            return i.mode === 'opt-out' ? true : false;
                        }
                    }

                    return true;
                }),
            ),
        );

        if (streams.length) {
            combineLatest(streams)
                .pipe(map((results) => results.every((r) => r === true)))
                .subscribe((r) => stream.next(r));
        }

        return stream.asObservable().pipe(distinctUntilChanged());
    }

    private buildConsentChecks(consentsChecks: ConsentItem[]): InitializedConsentItem[] {
        const items: InitializedConsentItem[] = [];

        for (const consentCheck of consentsChecks) {
            if (consentCheck.type !== 'tracking') {
                this.logger.warn('Consent', 'Unsupported ConsentCheck type!');
                continue;
            }

            if (consentCheck.consent === 'OneTrust') {
                const stream = this.oneTrust.changes();
                items.push({ ...consentCheck, result: stream });
            } else if (
                typeof consentCheck.consent === 'object' &&
                typeof consentCheck.consent?.handler === 'function'
            ) {
                const item = this.buildFunctionConsentChecker(consentCheck);
                items.push(item);
            } else {
                this.logger.warn('Consent', 'Unsupported ConsentChecker!');
            }
        }

        return items;
    }

    private buildFunctionConsentChecker(options: ConsentItem): InitializedConsentItem {
        const stream = new ReplaySubject<ConsentResult>();

        if (typeof options.consent === 'object' && typeof options.consent?.handler === 'function') {
            const result = options.consent.handler(stream.next.bind(stream));

            if (typeof result === 'object' && isObservable(result)) {
                result.subscribe(stream);
            } else if (this.isPromise<ConsentResult>(result)) {
                from(result).subscribe(stream);
            } else if (this.isValidResult(result)) {
                stream.next(result as ConsentResult);
            }
        }

        return {
            ...options,
            result: stream.pipe(filter((r) => this.isValidResult(r))),
        };
    }

    private isPromise<T>(r: any): r is Promise<T> {
        return r instanceof Promise || (typeof r === 'object' && typeof r?.then === 'function');
    }

    private isValidResult(result: any): result is ConsentResult {
        const r = result as ConsentResult;

        return typeof r === 'object' && (r?.state === 'pending' || r?.state === 'responded');
    }
}
